/**
 * Challenge System — Interactive tour step validators
 *
 * Apps register their own challenges via the OnboardingProvider config.
 */

import type { Challenge } from './types';

const challengeRegistry = new Map<string, Challenge>();

export function registerChallenge(challenge: Challenge): void {
  challengeRegistry.set(challenge.id, challenge);
}

export function registerChallenges(challenges: Challenge[]): void {
  challenges.forEach(registerChallenge);
}

export function getChallenge(id: string): Challenge | undefined {
  return challengeRegistry.get(id);
}

export function clearChallenges(): void {
  challengeRegistry.clear();
}

export function isPanelVisible(panelId: string): boolean {
  const element = document.querySelector(`[data-tour="panel-${panelId}"]`);
  if (element) {
    return isElementVisible(element);
  }
  const byId = document.getElementById(panelId);
  if (byId) {
    return isElementVisible(byId);
  }
  return false;
}

export function isElementVisible(elementOrSelector: string | Element): boolean {
  const element = typeof elementOrSelector === 'string'
    ? document.querySelector(elementOrSelector)
    : elementOrSelector;

  if (!element) return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

export function getPanelDisplayName(panelId: string): string {
  const names: Record<string, string> = {
    dag: 'DAG',
    data: 'Data Explorer',
    plot: 'Plot Builder',
    'saved-plots': 'Saved Plots',
    detail: 'Artifact Detail',
    viewer: 'Flow360 Viewer',
    cockpit: 'Cockpit',
    changelog: 'Changelog',
    comments: 'Comments',
    conversations: 'Conversations',
  };
  return names[panelId] ?? panelId.charAt(0).toUpperCase() + panelId.slice(1).replace(/-/g, ' ');
}

export function getParentPanel(selector: string): string | null {
  const element = document.querySelector(selector);
  if (!element) return null;

  let current: Element | null = element;
  while (current) {
    const tourAttr = current.getAttribute('data-tour');
    if (tourAttr?.startsWith('panel-')) {
      return tourAttr.replace('panel-', '');
    }
    current = current.parentElement;
  }
  return null;
}

export function buildElementNotVisibleHTML(panelId: string | null): string {
  const panelName = panelId ? getPanelDisplayName(panelId) : 'the required panel';
  return `
    <div class="tour-element-warning">
      <span class="tour-element-warning__icon">⚠</span>
      <span>Open <strong>${panelName}</strong> to see this element</span>
    </div>
  `;
}

export function createValueChallenge(options: {
  id: string;
  instruction: string;
  hint?: string;
  targetValue?: string;
  requiredPanel?: string;
  getValue: () => unknown;
  expectedValue: unknown;
  noSkip?: boolean;
}): Challenge {
  return {
    id: options.id,
    instruction: options.instruction,
    hint: options.hint,
    targetValue: options.targetValue,
    requiredPanel: options.requiredPanel,
    noSkip: options.noSkip,
    validate: () => options.getValue() === options.expectedValue,
  };
}

export function createPresenceChallenge(options: {
  id: string;
  instruction: string;
  hint?: string;
  requiredPanel?: string;
  selector: string;
  noSkip?: boolean;
}): Challenge {
  return {
    id: options.id,
    instruction: options.instruction,
    hint: options.hint,
    requiredPanel: options.requiredPanel,
    noSkip: options.noSkip,
    validate: () => isElementVisible(options.selector),
  };
}
